import React from "react";
import {Card, Col, Empty, Row, Statistic, Table, Typography} from 'antd';
import {
    BarChartOutlined,
    CalendarOutlined,
    CrownOutlined,
    FlagOutlined,
    StarOutlined,
    TeamOutlined,
    TrophyOutlined
} from '@ant-design/icons';
import AvatarDisplay from '../components/AvatarDisplay';

const { Title, Text } = Typography;

const LeaderBoard = ({ players }) => {
    const sortedPlayers = [...players].sort((a, b) => b.totalScore - a.totalScore);
    const currentDate = new Date().toLocaleDateString('pl-PL');

    const podiumMedals = ['🥇', '🥈', '🥉'];
    const podiumColors = ['#FFD700', '#C0C0C0', '#CD7F32'];

    // Kolumny tabeli rankingu
    const rankingColumns = [
        {
            title: 'Poz.',
            key: 'position',
            width: 60,
            render: (_, __, index) => (
                <Text strong style={{
                    color: index < 3 ? podiumColors[index] : 'var(--color-white)',
                    fontSize: index < 3 ? '16px' : '14px'
                }}>
                    {index + 1}
                </Text>
            ),
        },
        {
            title: 'Grupa',
            key: 'player',
            render: (player) => (
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <AvatarDisplay
                        avatarId={
                            player.avatar?.numericId ||
                            player.avatarId ||
                            1
                        }
                        size={32}
                        showBorder={true}
                    />
                    <div>
                        <Text style={{color: 'var(--color-white)', fontWeight: 600, display: 'block'}}>
                            "{player.groupName || 'Grupa'}"
                        </Text>
                        {player.playerNames && player.playerNames.length > 0 && (
                            <Text style={{color: 'var(--white-60)', fontSize: '12px'}}>
                                {player.playerNames.join(', ')}
                            </Text>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: 'Punkty',
            key: 'points',
            width: 80,
            render: (player) => (
                <Text strong style={{color: 'var(--color-white)', fontSize: '16px'}}>
                    {player.totalScore}
                </Text>
            ),
        },
        {
            title: 'Dołki',
            key: 'holes',
            width: 80,
            render: (player) => (
                <Text style={{color: 'var(--white-80)'}}>
                    {player.holes?.length || 0}/18
                </Text>
            ),
        },
        {
            title: 'Średnia',
            key: 'average',
            width: 80,
            render: (player) => (
                <Text style={{color: 'var(--white-80)'}}>
                    {(player.holes?.length || 0) > 0
                        ? (player.totalScore / player.holes.length).toFixed(1)
                        : '0.0'
                    }
                </Text>
            ),
        },
        {
            title: 'Ostatnia aktywność',
            key: 'lastActivity',
            render: (player) => (
                <Text style={{color: 'var(--white-60)', fontSize: '12px'}}>
                    {player.lastActivity
                        ? new Date(player.lastActivity).toLocaleTimeString('pl-PL')
                        : new Date(player.loginTime).toLocaleTimeString('pl-PL')
                    }
                </Text>
            ),
        },
    ];

    return (
        <div className="container">
            <div className="overlay-gradient"></div>
            <div className="layout-grid" style={{position: 'relative', zIndex: 1}}>

                {/* Header */}
                <div className="grid-span-8" style={{textAlign: 'center', marginBottom: 32}}>
                    <Title level={1} className="tablet" style={{color: 'var(--color-white)', margin: 0}}>
                        <TrophyOutlined style={{marginRight: 16, color: '#FFD700'}} />
                        Tablica Wyników
                    </Title>
                    <Text style={{color: 'var(--white-60)', fontSize: '18px'}}>
                        <CalendarOutlined style={{marginRight: 8}} />
                        {currentDate}
                    </Text>
                </div>

                {sortedPlayers.length === 0 ? (
                    /* No Players */
                    <div className="grid-span-8">
                        <Card className="card center" style={{minHeight: '50vh'}}>
                            <Empty
                                image={<TeamOutlined style={{fontSize: 64, color: 'var(--white-40)'}} />}
                                description={
                                    <div style={{color: 'var(--color-white)'}}>
                                        <Title level={3} style={{color: 'var(--color-white)', margin: '16px 0'}}>
                                            Brak aktywnych grup
                                        </Title>
                                        <Text style={{color: 'var(--white-60)', fontSize: '16px'}}>
                                            Zaloguj się w recepcji, aby rozpocząć grę!
                                        </Text>
                                    </div>
                                }
                            />
                        </Card>
                    </div>
                ) : (
                    <>
                        {/* Podium - Top 3 */}
                        {sortedPlayers.length >= 1 && (
                            <div className="grid-span-8" style={{marginBottom: 32}}>
                                <Card className="card">
                                    <Title level={3} style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: 24}}>
                                        <CrownOutlined style={{marginRight: 8, color: '#FFD700'}} />
                                        🏆 Podium
                                    </Title>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: sortedPlayers.length === 1 ? '1fr' :
                                            sortedPlayers.length === 2 ? 'repeat(2, 1fr)' :
                                                'repeat(3, 1fr)',
                                        gap: '24px',
                                        maxWidth: '800px',
                                        margin: '0 auto'
                                    }}>
                                        {sortedPlayers.slice(0, 3).map((player, index) => {
                                            const position = index + 1;
                                            return (
                                                <div
                                                    key={player.id}
                                                    style={{
                                                        textAlign: 'center',
                                                        padding: '24px 16px',
                                                        background: `linear-gradient(135deg, ${podiumColors[index]}20, ${podiumColors[index]}10)`,
                                                        border: `2px solid ${podiumColors[index]}40`,
                                                        borderRadius: 'var(--radius-12)',
                                                        position: 'relative',
                                                        transform: index === 0 ? 'scale(1.05)' : 'scale(1)',
                                                        transition: 'transform 0.2s ease'
                                                    }}
                                                >
                                                    {/* Medal */}
                                                    <div style={{
                                                        fontSize: '48px',
                                                        marginBottom: '12px',
                                                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                                                    }}>
                                                        {podiumMedals[index]}
                                                    </div>

                                                    {/* Avatar */}
                                                    <div style={{
                                                        marginBottom: '12px',
                                                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                                                    }}>
                                                        <AvatarDisplay
                                                            avatarId={
                                                                player.avatar?.numericId ||
                                                                player.avatarId ||
                                                                1
                                                            }
                                                            size={index === 0 ? 64 : 56}
                                                            showBorder={true}
                                                        />
                                                    </div>

                                                    {/* Group Name */}
                                                    <Title level={4} style={{
                                                        color: 'var(--color-white)',
                                                        margin: '0 0 4px 0',
                                                        fontSize: index === 0 ? '18px' : '16px'
                                                    }}>
                                                        "{player.groupName || 'Grupa'}"
                                                    </Title>

                                                    {/* Player Names */}
                                                    {player.playerNames && player.playerNames.length > 0 && (
                                                        <Text style={{
                                                            color: 'var(--white-60)',
                                                            fontSize: '12px',
                                                            display: 'block',
                                                            marginBottom: '8px'
                                                        }}>
                                                            {player.playerNames.slice(0, 2).join(', ')}
                                                            {player.playerNames.length > 2 && ` (+${player.playerNames.length - 2})`}
                                                        </Text>
                                                    )}

                                                    {/* Score */}
                                                    <Text style={{
                                                        color: podiumColors[index],
                                                        fontSize: index === 0 ? '24px' : '20px',
                                                        fontWeight: 'bold',
                                                        display: 'block',
                                                        marginBottom: '4px'
                                                    }}>
                                                        {player.totalScore} pkt
                                                    </Text>

                                                    {/* Holes */}
                                                    <Text style={{color: 'var(--white-60)', fontSize: '14px'}}>
                                                        {player.holes?.length || 0}/18 dołków
                                                    </Text>

                                                    {/* Position badge */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '-10px',
                                                        right: '-10px',
                                                        background: podiumColors[index],
                                                        color: 'var(--color-dark-green)',
                                                        width: '30px',
                                                        height: '30px',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '14px',
                                                        fontWeight: 'bold',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                                    }}>
                                                        {position}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>
                            </div>
                        )}

                        {/* Full Ranking Table */}
                        <div className="grid-span-8" style={{marginBottom: 32}}>
                            <Card className="card">
                                <Title level={3} style={{color: 'var(--color-white)', marginBottom: 24}}>
                                    <BarChartOutlined style={{marginRight: 8}} />
                                    📋 Pełny ranking
                                </Title>

                                <Table
                                    columns={rankingColumns}
                                    dataSource={sortedPlayers.map((player, index) => ({
                                        ...player,
                                        key: player.id
                                    }))}
                                    pagination={false}
                                    size="middle"
                                    rowClassName={(record, index) =>
                                        index < 3 ? 'ranking-row-podium' : 'ranking-row'
                                    }
                                />
                            </Card>
                        </div>

                        {/* Statistics */}
                        <div className="grid-span-8">
                            <Card className="card">
                                <Title level={3} style={{color: 'var(--color-white)', marginBottom: 24}}>
                                    <BarChartOutlined style={{marginRight: 8}} />
                                    📊 Statystyki dnia
                                </Title>

                                <Row gutter={[24, 24]}>
                                    <Col xs={24} sm={8}>
                                        <div className="stat-card" style={{textAlign: 'center'}}>
                                            <div style={{
                                                background: 'var(--white-10)',
                                                borderRadius: 'var(--radius-8)',
                                                padding: '24px 16px',
                                                border: '1px solid var(--white-30)'
                                            }}>
                                                <TeamOutlined style={{
                                                    fontSize: '32px',
                                                    color: 'var(--color-light-green)',
                                                    marginBottom: '8px',
                                                    display: 'block'
                                                }} />
                                                <Statistic
                                                    value={players.length}
                                                    valueStyle={{
                                                        color: 'var(--color-white)',
                                                        fontSize: '32px',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                                <Text style={{color: 'var(--white-60)', fontSize: '14px'}}>
                                                    Aktywne grupy
                                                </Text>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col xs={24} sm={8}>
                                        <div className="stat-card" style={{textAlign: 'center'}}>
                                            <div style={{
                                                background: 'var(--white-10)',
                                                borderRadius: 'var(--radius-8)',
                                                padding: '24px 16px',
                                                border: '1px solid var(--white-30)'
                                            }}>
                                                <FlagOutlined style={{
                                                    fontSize: '32px',
                                                    color: 'var(--color-light-green)',
                                                    marginBottom: '8px',
                                                    display: 'block'
                                                }} />
                                                <Statistic
                                                    value={players.reduce((sum, p) => sum + (p.holes?.length || 0), 0)}
                                                    valueStyle={{
                                                        color: 'var(--color-white)',
                                                        fontSize: '32px',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                                <Text style={{color: 'var(--white-60)', fontSize: '14px'}}>
                                                    Rozegrane dołki
                                                </Text>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col xs={24} sm={8}>
                                        <div className="stat-card" style={{textAlign: 'center'}}>
                                            <div style={{
                                                background: 'var(--white-10)',
                                                borderRadius: 'var(--radius-8)',
                                                padding: '24px 16px',
                                                border: '1px solid var(--white-30)'
                                            }}>
                                                <StarOutlined style={{
                                                    fontSize: '32px',
                                                    color: '#FFD700',
                                                    marginBottom: '8px',
                                                    display: 'block'
                                                }} />
                                                <Statistic
                                                    value={sortedPlayers.length > 0 ? sortedPlayers[0].totalScore : 0}
                                                    valueStyle={{
                                                        color: 'var(--color-white)',
                                                        fontSize: '32px',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                                <Text style={{color: 'var(--white-60)', fontSize: '14px'}}>
                                                    Najlepszy wynik
                                                </Text>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LeaderBoard;